import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PostDetailPage = () => {
  const { user, logout } = useContext(AuthContext);
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/posts/${postId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setPost(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setError('premium');
      } else if (error.response?.status === 404) {
        setError('notfound');
      } else {
        setError('error');
      }
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-content">
          <a href="/" className="navbar-logo" data-testid="navbar-logo">LE REGISTRE</a>
          <div className="navbar-menu">
            {user ? (
              <>
                {user.role === 'admin' && (
                  <a href="/admin" className="navbar-link" data-testid="admin-link">Console</a>
                )}
                <a href="/subscription" className="navbar-link" data-testid="subscription-link">Accès</a>
                <div className="navbar-user" data-testid="user-menu">
                  {user.picture && <img src={user.picture} alt={user.name} className="user-avatar" data-testid="user-avatar" />}
                  <span className="user-name" data-testid="user-name">{user.name}</span>
                  {user.subscription_status === 'premium' && (
                    <span className="premium-badge" data-testid="premium-badge">Accès étendu</span>
                  )}
                  <button onClick={logout} className="btn btn-outline" data-testid="logout-button">Déconnexion</button>
                </div>
              </>
            ) : (
              <button onClick={() => navigate('/')} className="btn btn-primary" data-testid="login-button">Se connecter</button>
            )}
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="content-container">
        <button 
          onClick={() => navigate('/')} 
          className="btn btn-secondary" 
          style={{ marginBottom: '2rem' }}
          data-testid="back-button"
        >
          ← Retour au journal
        </button>

        {loading ? (
          <div className="loading-screen" style={{ minHeight: '400px' }}>
            <div className="loading-spinner"></div>
          </div>
        ) : error === 'premium' ? (
          <div className="post-detail-error" data-testid="premium-error">
            <div className="lock-icon" style={{ marginBottom: '1.5rem' }}>▪ ▪ ▪</div>
            <h2>Entrée restreinte</h2>
            <p>
              Cette entrée fait partie des archives restreintes du Registre. L'accès étendu est requis pour la consulter.
            </p>
            <button onClick={() => navigate('/subscription')} className="btn btn-primary" data-testid="subscribe-button">
              Étendre l'accès
            </button>
          </div>
        ) : error === 'notfound' ? (
          <div className="post-detail-error" data-testid="notfound-error">
            <h2>Entrée introuvable</h2>
            <p>
              Cette entrée n'existe pas ou a été retirée du Registre.
            </p>
            <button onClick={() => navigate('/')} className="btn btn-primary" data-testid="home-button">
              Retour au journal
            </button>
          </div>
        ) : error ? (
          <div className="post-detail-error" data-testid="error-message">
            <h2>Erreur de lecture</h2>
            <p>
              Une erreur s'est produite lors du chargement de l'entrée.
            </p>
            <button onClick={() => navigate('/')} className="btn btn-primary">
              Retour au journal
            </button>
          </div>
        ) : post ? (
          <article className="post-detail" data-testid="post-detail">
            {post.is_premium && (
              <div className="premium-badge" style={{ marginBottom: '1rem', display: 'inline-block' }}>
                Archive restreinte
              </div>
            )}
            <h1 className="post-detail-title" data-testid="post-title">{post.title}</h1>
            <div className="post-detail-meta" data-testid="post-meta">
              <span className="post-date">{formatDate(post.created_at)}</span>
            </div>
            
            {post.media_url && (
              <div className="post-detail-media" data-testid="post-media">
                {post.media_type === 'video' ? (
                  <video controls className="post-detail-video">
                    <source src={post.media_url} type="video/mp4" />
                    Votre navigateur ne supporte pas la vidéo.
                  </video>
                ) : (
                  <img src={post.media_url} alt={post.title} className="post-detail-image" />
                )}
              </div>
            )}
            
            <div className="post-detail-content" data-testid="post-content" dangerouslySetInnerHTML={{ __html: post.content }} />
            
            {post.gallery && post.gallery.length > 0 && (
              <div className="post-detail-gallery" data-testid="post-gallery">
                {post.gallery.map((imageUrl, index) => (
                  <div key={index} className="post-detail-gallery-item" data-testid={`gallery-image-${index}`}>
                    <img src={imageUrl} alt={`${post.title} - Photo ${index + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </article>
        ) : null}
      </div>
    </div>
  );
};

export default PostDetailPage;
